/**
 * El score crediticio, explicado.
 *
 * Un número solo no sirve: la pantalla tiene que contestar «¿por qué esta calificación?» sin que el
 * analista pregunte. Por eso el orden es el de una defensa ante un comité — el veredicto, qué lo
 * limitó, qué le resta puntos, sobre cuánta información se apoya, qué dato falta pedir, cómo venía
 * la empresa y qué pasa si las cosas empeoran.
 *
 * Y cada indicador que tiene evidencia se abre: el puntaje termina en la página de un documento,
 * igual que todo lo demás en este producto.
 */

import { useQuery } from "@tanstack/react-query";
import { api, friendlyError } from "../../api/client";
import { RECOMMENDATIONS, label } from "../../lib/labels";
import {
  componentRows,
  coverageLabel,
  formatNumber,
  gradeChip,
  indicatorValue,
  leverCombination,
  leverRows,
  stressRows,
  trendRows,
} from "../../lib/model";
import type { Tone } from "../../lib/model";
import type { EvidenceRequest } from "../../components/EvidenceDrawer";
import {
  Chip,
  Dot,
  EmptyState,
  ErrorBanner,
  SectionTitle,
  Spinner,
  Table,
  Td,
  Th,
} from "../../components/ui";

const TONE_TEXT: Record<Tone, string> = {
  good: "text-good",
  warn: "text-warn",
  bad: "text-bad",
  neutral: "text-muted",
};
const TONE_BAR: Record<Tone, string> = {
  good: "bg-good",
  warn: "bg-warn",
  bad: "bg-bad",
  neutral: "bg-neutral-line",
};

export default function Score({
  caseId,
  onEvidence,
}: {
  caseId: string;
  onEvidence: (request: EvidenceRequest) => void;
}) {
  const score = useQuery({
    queryKey: ["score", caseId],
    queryFn: () => api.getScore(caseId),
    retry: false,
  });

  if (score.isLoading) return <Spinner />;
  if (!score.data) {
    return (
      <EmptyState
        title="Todavía no hay score"
        text="Se calcula al analizar el legajo, aplicando la política del banco a las cifras extraídas."
      />
    );
  }

  const data = score.data;
  const chip = gradeChip(data);
  const components = componentRows(data);
  const knockouts = data.payload.knockouts ?? [];
  const reasons = data.payload.reasons ?? [];
  const missing = data.payload.missing ?? [];
  const trends = trendRows(data);
  const stress = stressRows(data);
  const levers = leverRows(data);
  const combination = leverCombination(data);
  const blocked = data.payload.levers?.blocked_by_knockouts ?? [];

  return (
    <>
      {/* El veredicto */}
      <div className="flex flex-wrap items-start gap-8 rounded-xl border border-line p-5">
        <div>
          <div className="text-[0.7rem] font-semibold tracking-wider text-muted uppercase">
            Calificación
          </div>
          <div className="mt-1 flex items-baseline gap-3">
            <span className={`text-5xl font-bold ${TONE_TEXT[chip?.tone ?? "neutral"]}`}>
              {data.grade ?? "—"}
            </span>
            <span className="text-2xl font-semibold tabular-nums">
              {data.score === null ? "—" : formatNumber(data.score, 1)}
              <span className="text-base font-normal text-muted"> / 100</span>
            </span>
          </div>
          <div className="mt-1 text-sm text-muted">{data.grade_label ?? ""}</div>
        </div>

        <div className="min-w-56 flex-1">
          <div className="text-[0.7rem] font-semibold tracking-wider text-muted uppercase">
            Sobre cuánta información
          </div>
          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-line">
            <div
              className={`h-full rounded-full ${data.partial ? "bg-warn" : "bg-good"}`}
              style={{ width: `${Math.max(data.coverage * 100, 2)}%` }}
            />
          </div>
          <div className="mt-1.5 text-sm">
            {coverageLabel(data.coverage, data.partial)}
          </div>
          {data.partial ? (
            <p className="mt-1 text-xs text-warn">
              Con esta cobertura el sistema informa el puntaje pero no respalda una recomendación.
            </p>
          ) : null}
        </div>

        <div>
          <div className="text-[0.7rem] font-semibold tracking-wider text-muted uppercase">
            Recomendación de la política
          </div>
          <div className="mt-1.5">
            <Chip text={label(RECOMMENDATIONS, data.recommendation)} tone={chip?.tone ?? "neutral"} />
          </div>
          <div className="mt-2 text-xs text-muted">
            Política {data.policy_version}
            {data.provisional ? " · provisoria, pendiente de validación" : ""}
          </div>
        </div>
      </div>

      {/* Lo que limitó la calificación */}
      {knockouts.length ? (
        <>
          <SectionTitle hint="Hechos graves que limitan la calificación por más que el resto esté bien. El puntaje sin topes habría sido otro.">
            Topes aplicados
          </SectionTitle>
          <ul className="space-y-2">
            {knockouts.map((k) => (
              <li key={k.key} className="rounded-lg border border-bad-line bg-bad-bg p-3">
                <div className="text-sm font-medium text-bad">{k.label}</div>
                <p className="mt-0.5 text-sm text-ink/80">{k.explanation}</p>
              </li>
            ))}
          </ul>
          {data.raw_score !== null && data.raw_score !== data.score ? (
            <p className="mt-2 text-sm text-muted">
              Sin topes el puntaje habría sido{" "}
              <strong className="text-ink">{formatNumber(data.raw_score, 1)}</strong>.
            </p>
          ) : null}
        </>
      ) : null}

      {/* Por qué este número */}
      {reasons.length ? (
        <>
          <SectionTitle hint="Los indicadores que más puntos le restan, en orden.">
            Por qué esta calificación
          </SectionTitle>
          <ul className="space-y-2">
            {reasons.map((r) => (
              <li key={r.key} className="flex items-baseline justify-between gap-4 border-b border-line pb-2">
                <span className="text-sm">
                  <strong>{r.label}</strong>
                  {r.band ? <span className="text-muted"> · banda «{r.band}»</span> : null}
                </span>
                <span className="shrink-0 text-sm tabular-nums text-bad">
                  −{formatNumber(r.points_lost, 1)} pts
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {/* De dónde salen los puntos */}
      <SectionTitle hint="Hacé clic en un indicador para ver la cifra y la página de la que salió.">
        Aporte de cada componente
      </SectionTitle>
      <div className="space-y-5">
        {components.map((c) => (
          <div key={c.key}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-sm font-medium">
                {c.label}
                <span className="ml-2 font-normal text-muted">peso {Math.round(c.weight * 100)}%</span>
              </span>
              <span className={`text-sm tabular-nums ${TONE_TEXT[c.tone]}`}>
                {c.score === null ? "sin datos" : `${formatNumber(c.score, 0)}/100`}
              </span>
            </div>
            <p className="mt-0.5 text-xs text-muted">{c.question}</p>
            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-line">
              <div
                className={`h-full rounded-full ${TONE_BAR[c.tone]}`}
                style={{ width: `${c.score === null ? 0 : c.score}%` }}
              />
            </div>

            <div className="mt-2">
              <Table>
                <thead>
                  <tr>
                    <Th>Indicador</Th>
                    <Th right>Valor</Th>
                    <Th>Lectura</Th>
                    <Th right>Puntos</Th>
                    <Th>Fuente</Th>
                  </tr>
                </thead>
                <tbody>
                  {c.indicators.map((i) => {
                    const clickable = !!i.evidence_id;
                    const open = () =>
                      clickable &&
                      onEvidence({
                        evidenceId: i.evidence_id!,
                        title: i.label,
                        caseId,
                        fieldName: i.field,
                        period: i.period,
                      });
                    return (
                      <tr
                        key={i.key}
                        tabIndex={clickable ? 0 : -1}
                        onClick={open}
                        onKeyDown={(e) => {
                          if (clickable && (e.key === "Enter" || e.key === " ")) {
                            e.preventDefault();
                            open();
                          }
                        }}
                        className={clickable ? "cursor-pointer hover:bg-canvas" : ""}
                      >
                        <Td className="font-medium">{i.label}</Td>
                        <Td right>{indicatorValue(i)}</Td>
                        <Td className="text-muted">
                          {i.band ?? <span className="text-warn">{i.missing_note || "sin dato"}</span>}
                        </Td>
                        <Td right className="tabular-nums text-muted">
                          {i.points === null ? "—" : formatNumber(i.points, 0)}
                        </Td>
                        <Td className="text-muted">
                          {i.value === null ? "—" : i.source === "externa" ? "BCRA" : "Legajo"}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </div>
        ))}
      </div>

      {/* Qué pedirle al cliente */}
      {missing.length ? (
        <>
          <SectionTitle hint="Ordenado por cuánto del análisis destrabaría cada dato. Es la llamada que conviene hacer primero.">
            Qué falta pedir
          </SectionTitle>
          <ul className="space-y-2">
            {missing.map((m) => (
              <li key={m.field} className="flex items-baseline justify-between gap-4 border-b border-line pb-2">
                <span className="text-sm">
                  <strong>{m.label}</strong>
                  <span className="block text-xs text-muted">{m.note}</span>
                </span>
                <span className="shrink-0 text-sm tabular-nums text-muted">
                  destraba {Math.round(m.weight * 100)}%
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {/* Qué habría que cambiar */}
      {levers.length || blocked.length ? (
        <>
          <SectionTitle
            hint={
              data.payload.levers?.points_to_next_grade != null && data.payload.levers.next_grade
                ? `Faltan ${formatNumber(data.payload.levers.points_to_next_grade, 1)} puntos para el grado ${data.payload.levers.next_grade}.`
                : undefined
            }
          >
            Qué tendría que cambiar
          </SectionTitle>

          {blocked.length ? (
            <p className="rounded-lg border border-bad-line bg-bad-bg p-3 text-sm">
              Con {blocked.length === 1 ? "el tope" : "los topes"} de{" "}
              <strong>{blocked.join(", ").toLowerCase()}</strong> ninguna mejora de indicadores
              levanta la calificación: primero hay que revertir el hecho que los dispara.
            </p>
          ) : null}

          {levers.length ? (
            <Table>
              <thead>
                <tr>
                  <Th>Indicador</Th>
                  <Th right>Hoy</Th>
                  <Th>Habría que</Th>
                  <Th right>Sube</Th>
                  <Th>Quedaría en</Th>
                </tr>
              </thead>
              <tbody>
                {levers.map((l) => (
                  <tr key={l.key}>
                    <Td className="font-medium">
                      {l.label}
                      <span className="block text-xs text-muted">
                        de «{l.band_from}» a «{l.band_to}»
                      </span>
                    </Td>
                    <Td right className="text-muted">
                      {l.value}
                    </Td>
                    <Td>
                      {l.direction} a <strong>{l.target}</strong>
                    </Td>
                    <Td right className="tabular-nums text-good">
                      +{formatNumber(l.gain, 1)}
                    </Td>
                    <Td>
                      {formatNumber(l.score_after, 1)}
                      {l.changes_grade ? (
                        <span className="ml-1.5 font-semibold text-good">
                          grado {l.grade_after}
                        </span>
                      ) : (
                        <span className="ml-1.5 text-muted">grado {l.grade_after}</span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}

          {combination ? (
            <div className="mt-3 rounded-lg border border-line p-3">
              <div className="text-[0.7rem] font-semibold tracking-wider text-muted uppercase">
                Ninguna sola alcanza · la combinación más corta que sí
              </div>
              <ul className="mt-1.5 space-y-1 text-sm">
                {combination.steps.map((l) => (
                  <li key={l.key}>
                    <strong>{l.label}</strong>: {l.direction} de {l.value} a {l.target}{" "}
                    <span className="text-muted">(+{formatNumber(l.gain, 1)})</span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-sm">
                Las dos cosas juntas suman{" "}
                <strong className="text-good">+{formatNumber(combination.total, 1)}</strong> puntos y
                lo llevan al grado <strong>{combination.reaches}</strong>.
              </p>
            </div>
          ) : null}
        </>
      ) : null}

      {/* Cómo venía */}
      {trends.length ? (
        <>
          <SectionTitle
            hint={`Contra el ejercicio ${data.payload.trends?.previous_period ?? "anterior"}. Un nivel dentro de umbral que empeoró fuerte es un hallazgo igual.`}
          >
            Trayectoria
          </SectionTitle>
          <Table>
            <thead>
              <tr>
                <Th>Indicador</Th>
                <Th right>Antes</Th>
                <Th right>Ahora</Th>
                <Th right>Variación</Th>
                <Th>Lectura</Th>
              </tr>
            </thead>
            <tbody>
              {trends.map((t) => (
                <tr key={t.field}>
                  <Td className="font-medium">{t.label}</Td>
                  <Td right className="text-muted">{formatNumber(t.previous, 2)}</Td>
                  <Td right>{formatNumber(t.current, 2)}</Td>
                  <Td right className="tabular-nums">
                    {t.change === null ? "—" : `${t.change > 0 ? "+" : ""}${Math.round(t.change * 100)}%`}
                  </Td>
                  <Td>
                    <span className="flex items-center gap-2">
                      <Dot tone={t.direction === "deterioro" ? "bad" : "good"} />
                      {t.direction}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      ) : null}

      {/* Y si las cosas empeoran */}
      {stress.length ? (
        <>
          <SectionTitle hint={data.payload.stress?.reason}>
            Si las cosas empeoran
          </SectionTitle>
          <ul className="space-y-2">
            {stress.map((s) => (
              <li
                key={s.key}
                className={`rounded-lg border p-3 ${s.holds ? "border-line" : "border-bad-line bg-bad-bg"}`}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm font-medium">{s.label}</span>
                  <Chip
                    text={s.holds ? "Aguanta" : "No aguanta"}
                    tone={s.holds ? "good" : "bad"}
                  />
                </div>
                <p className="mt-0.5 text-xs text-muted">{s.question}</p>
                <p className="mt-1 text-sm">{s.note}</p>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {score.isError ? <ErrorBanner {...friendlyError(score.error)} /> : null}
    </>
  );
}
